/**
 * UploadDialog Component
 * Modal dialog for uploading documents — wage slips, discharge books, P60s, contracts, etc.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Upload, Loader2, Lock, Eye, EyeOff } from 'lucide-react';

const DOCUMENT_TYPES = [
  { value: 'wage_slip', label: 'Payslip / Wage Slip', monthly: true },
  { value: 'p60', label: 'P60', monthly: false },
  { value: 'p45', label: 'P45', monthly: false },
  { value: 'discharge_book', label: 'Discharge Book / CDC', monthly: false },
  { value: 'contract', label: 'Employment Contract', monthly: false },
  { value: 'bank_statement', label: 'Bank Statement', monthly: true },
  { value: 'pension_statement', label: 'Pension Statement', monthly: false },
  { value: 'p11d', label: 'P11D', monthly: false },
  { value: 'travel_receipt', label: 'Travel Receipt', monthly: true },
  { value: 'other', label: 'Other Document', monthly: false },
];

const UploadDialog = ({ 
  open, 
  onClose, 
  selectedMonth, 
  onMonthChange, 
  monthOptions,
  onFileSelect,
  uploading,
  fileInputRef,
  pdfPassword,
  onPasswordChange,
  documentType,
  onDocumentTypeChange
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const selectedDocType = DOCUMENT_TYPES.find(d => d.value === documentType) || DOCUMENT_TYPES[0];
  const needsMonth = selectedDocType.monthly;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white">Upload Document</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Document Type Selector */}
          <div className="space-y-2">
            <Label className="text-slate-300">Document Type</Label>
            <Select value={documentType || 'wage_slip'} onValueChange={(val) => {
              if (onDocumentTypeChange) onDocumentTypeChange(val);
              const dt = DOCUMENT_TYPES.find(d => d.value === val);
              if (dt && !dt.monthly && selectedMonth !== 'annual') {
                onMonthChange('annual');
              }
            }}>
              <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white" data-testid="doc-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {DOCUMENT_TYPES.map(dt => (
                  <SelectItem key={dt.value} value={dt.value} className="text-white hover:bg-slate-700">
                    {dt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Month Selector — show for monthly docs, auto-set "Annual" for annual docs */}
          <div className="space-y-2">
            <Label className="text-slate-300">{needsMonth ? 'Earnings Month' : 'Period'}</Label>
            <Select value={selectedMonth || ''} onValueChange={onMonthChange}>
              <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="annual" className="text-white hover:bg-slate-700 font-medium">
                  Annual (Full Tax Year)
                </SelectItem>
                {monthOptions.map(({ value, label }) => (
                  <SelectItem 
                    key={value} 
                    value={value}
                    className="text-white hover:bg-slate-700"
                  >
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">
              {needsMonth 
                ? 'Select the month the earnings are FOR, not the payslip date'
                : 'Select "Annual" for documents that cover the whole tax year'}
            </p>
          </div>

          {/* PDF Password field */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-slate-400" />
              <Label className="text-slate-300">PDF Password (if protected)</Label>
            </div>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={pdfPassword || ''}
                onChange={(e) => onPasswordChange(e.target.value)}
                placeholder="Leave blank if not password protected"
                className="bg-slate-700/50 border-slate-600 text-white pr-10"
                data-testid="pdf-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-300">Upload File</Label>
            <div 
              className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-slate-500 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 text-amber-400 animate-spin" />
                  <p className="text-sm text-slate-400">Uploading...</p>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-slate-500 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">
                    Click to select a file
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Supported: PDF, JPG, PNG, DOCX
                  </p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx"
              className="hidden"
              onChange={onFileSelect}
              disabled={(!selectedMonth && needsMonth) || uploading}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={onClose}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadDialog;
